import { useEffect, useMemo } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { Autocomplete, CircularProgress, Container, TextField } from '@mui/material'
import * as yup from 'yup'
import Dialog from 'components/dialog/dialog'
import Loading from 'components/loading/loading'
import Notification from 'components/notification'
import GLOBAL from 'modules/global'
import type { PartialUpdateConfigItemProps } from 'modules/partial'
import type { DetailStateProps, ErrorProps, notificationProps } from 'modules/types'
import GlobalStyle from 'modules/styles'
import { useGetConfigItemDetailQuery, useUpdateConfigItemMutation } from 'store/configItem'
import { useGetVoucherBirthdayDropdownMutation } from 'store/tokenRequest'
import DialogStyle from './style'

const Edit = ({ open, onClose, id }: DetailStateProps & { id: number }) => {
  const { data, isLoading, isFetching } = useGetConfigItemDetailQuery(id)
  const isOddEven = data?.name.startsWith('ODD_EVEN_ALERT') && !data?.name.endsWith('_CTA')
  const notificationContent: notificationProps = isOddEven ? JSON.parse(data?.value || '{}') : null

  const [updateConfigItem, update] = useUpdateConfigItemMutation()
  const [getVoucherBirthdayDropdown, voucherBirthdayDropdown] =
    useGetVoucherBirthdayDropdownMutation()

  const scheme = yup.object<PartialUpdateConfigItemProps>({
    Value: yup.string().nullable().optional(),
    Description: yup.string().required('Description is required')
  })

  const formik = useFormik<PartialUpdateConfigItemProps>({
    validationSchema: scheme,
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      Id: (data && data.id) || 0,
      Name: (data && data.name) || '',
      Category: (data && data.configGroupName) || '',
      Description: (data && data.description) || '',
      Value: (data && data.value) || '',
      Label: (data && data.label) || '',
      Headline: (notificationContent?.content && notificationContent?.content?.headline) || '',
      SubHeadline: (notificationContent?.content && notificationContent?.content?.subheadline) || ''
    },
    onSubmit: (values: PartialUpdateConfigItemProps) => handleSubmit(values)
  })

  const handleSubmit = (e: PartialUpdateConfigItemProps) => {
    if (!isOddEven) {
      updateConfigItem({ id: e.Id, value: e.Value ? e.Value : null, description: e.Description })
    } else {
      updateConfigItem({
        id: e.Id,
        value: JSON.stringify({ content: { headline: e.Headline, subheadline: e.SubHeadline } }),
        description: e.Description
      })
    }
  }

  // Check if this is a birthday voucher config
  const isBirthdayVoucherConfig = data?.name === 'BIRTHDAY_VOUCHER'

  const vouchers = useMemo(() => {
    const vouchers = (voucherBirthdayDropdown?.data || []).map((v) => ({
      id: v.id,
      name: v.name
    }))

    return [{ id: null, name: 'None' }, ...vouchers]
  }, [voucherBirthdayDropdown?.data])

  // Fetch voucher dropdown data on mount if this is a birthday voucher config
  useEffect(() => {
    if (isBirthdayVoucherConfig && vouchers.length <= 1) {
      getVoucherBirthdayDropdown()
    }
  }, [isBirthdayVoucherConfig, vouchers, getVoucherBirthdayDropdown])

  const isLoadingConfig = isLoading || isFetching

  return (
    <>
      <Dialog
        open={open}
        title='Update Config Item'
        onCancel={onClose}
        onSubmit={() => formik.handleSubmit()}
        loading={isLoading}
        isDisabled={!formik.isValid}
      >
        <Container {...DialogStyle.Container}>
          {isLoadingConfig ? (
            <Loading />
          ) : data ? (
            <FormikProvider value={formik}>
              <TextField
                id='Name'
                variant='outlined'
                label='Config Code'
                value={formik.values.Name}
                onBlur={formik.handleBlur}
                error={formik.touched && formik.touched.Name && Boolean(formik.errors.Name)}
                helperText={
                  formik.touched && formik.touched.Name && formik.errors && formik.errors.Name
                }
                fullWidth
              />
              <TextField
                id='Label'
                variant='outlined'
                label='Name'
                value={formik.values.Label}
                onBlur={formik.handleBlur}
                fullWidth
              />
              <TextField
                id='Category'
                variant='outlined'
                label='Category'
                value={formik.values.Category}
                onBlur={formik.handleBlur}
                fullWidth
              />
              <TextField
                id='Description'
                variant='outlined'
                label='Description'
                value={formik.values.Description}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={
                  formik.touched && formik.touched.Description && Boolean(formik.errors.Description)
                }
                helperText={
                  formik.touched &&
                  formik.touched.Description &&
                  formik.errors &&
                  formik.errors.Description
                }
                fullWidth
              />
              {isBirthdayVoucherConfig ? (
                <Autocomplete
                  key={voucherBirthdayDropdown.isLoading ? 'loading' : 'dropdown'}
                  options={vouchers || []}
                  getOptionLabel={(list) => (list.id == null ? 'None' : list.name)}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.id == value.id : false
                  }
                  value={vouchers?.find((item) => item?.id === +formik.values.Value || null)}
                  onChange={(_, selectedOption) =>
                    formik.setFieldValue('Value', selectedOption?.id || '')
                  }
                  ListboxProps={GlobalStyle.ListBox}
                  renderOption={(props, item) => (
                    <li {...props} key={item.id}>
                      {item.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='Value'
                      label='Value'
                      error={formik.touched && formik.touched.Value && Boolean(formik.errors.Value)}
                      helperText={
                        formik.touched &&
                        formik.touched.Value &&
                        formik.errors &&
                        formik.errors.Value
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {voucherBirthdayDropdown.isLoading && (
                              <CircularProgress color='inherit' size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              ) : null}

              {!isBirthdayVoucherConfig && !isOddEven && (
                <TextField
                  id='Value'
                  variant='outlined'
                  label='Value'
                  value={formik.values.Value}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  error={formik.touched && formik.touched.Value && Boolean(formik.errors.Value)}
                  helperText={
                    formik.touched && formik.touched.Value && formik.errors && formik.errors.Value
                  }
                  fullWidth
                />
              )}

              {isOddEven && (
                <>
                  <TextField
                    id='Headline'
                    variant='outlined'
                    label='Headline'
                    value={formik.values.Headline}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    error={
                      formik.touched && formik.touched.Headline && Boolean(formik.errors.Headline)
                    }
                    helperText={
                      formik.touched &&
                      formik.touched.Headline &&
                      formik.errors &&
                      formik.errors.Headline
                    }
                    fullWidth
                  />
                  <TextField
                    id='SubHeadline'
                    variant='outlined'
                    label='Sub Headline'
                    value={formik.values.SubHeadline}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    error={
                      formik.touched &&
                      formik.touched.SubHeadline &&
                      Boolean(formik.errors.SubHeadline)
                    }
                    helperText={
                      formik.touched &&
                      formik.touched.SubHeadline &&
                      formik.errors &&
                      formik.errors.SubHeadline
                    }
                    fullWidth
                  />
                </>
              )}
            </FormikProvider>
          ) : null}
        </Container>
      </Dialog>

      <Notification
        open={!update.isLoading && !update.isUninitialized}
        onClose={() => (update.isError ? update.reset() : location.reload())}
        isError={Boolean(update.error) && update.isError}
        message={GLOBAL.returnExceptionMessage(update.isError, update.error as ErrorProps)}
      />
    </>
  )
}

export default Edit
